import find from 'lodash/find';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import {
  all,
  delay,
  fork,
  put,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';

import {
  logCritical,
  logVerbose,
} from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { ACTOR_TYPES, DOCUMENT_STATUS } from '@savgroup-front-common/constants';
import { FILE_STATES } from '@savgroup-front-common/constants/shared';
import {
  ActionCreators as AttachmentsActionCreators,
  ActionTypes as AttachmentsActionTypes,
  Selectors as AttachmentsSelectors,
} from '@savgroup-front-common/core/domains/attachments';
import {
  ActionTypes as CarriersActionTypes,
  ActionCreators as CarriersCreators,
  Selectors as CarriersSelectors,
} from '@savgroup-front-common/core/domains/carriers';
import {
  ActionCreators as ClaimActionCreators,
  Selectors as ClaimsSelectors,
} from '@savgroup-front-common/core/domains/claims';
import * as ClaimActionTypes from '@savgroup-front-common/core/domains/claims/actionTypes';
import { useLoginStore } from '@savgroup-front-common/core/domains/login/useLoginStore';
import { storeActionCreators } from '@savgroup-front-common/core/domains/store';
import { ActionCreators as WorkflowCreators } from '@savgroup-front-common/core/domains/workflow';
import { fibonacci } from '@savgroup-front-common/core/helpers';

import { ActionCreators } from '../actions';
import {
  ActionTypes as CustomerActionTypes,
  ActionCreators as CustomerCreators,
  Selectors as CustomerSelectors,
} from '../Customer';
import { customerInfosSelector } from '../Customer/selectors';
import { loadOwnerProductInfo } from '../Orders/actionCreators';
import { loadOrdersDataWorker } from '../Orders/saga';

import { loadFileTrackingItemData } from './actionCreators';
import {
  APPROVE_REJECT_QUOTE,
  FORK_LOAD_FILE_TRACKING_PAGE,
  LOAD_CURRENT_FILE_TRACKING_ITEM_DATA,
  REHYDRATE_FILE_TRACKING_PAGE,
  SUBMIT_ADDITIONAL_INFO,
} from './actionTypes';

const MEDIUM_RELOAD_DELAY = 1500;
const MAX_STEP_COUNT = 6;

function* rehydrateFileTrackingInfoWorker({ payload }) {
  const { fileId } = payload;

  const ownerId = useLoginStore.getState().login.id;

  yield all([
    put(CustomerCreators.loadFileStates(fileId)),
    take(CustomerActionTypes.LOAD_FILE_STATES.END),
    put(CustomerCreators.loadCustomerFile({ fileId, ownerId })),
    take(CustomerActionTypes.LOAD_CUSTOMER_FILE.END),
    put(CustomerCreators.loadCustomerFileSummary({ fileId })),
    take(CustomerActionTypes.LOAD_CUSTOMER_FILE_SUMMARY.END),
    put(CustomerCreators.getAvailableFileActions(fileId)),
    take(CustomerActionTypes.LOAD_FILE_STATE_HISTORY_LINES.END),
    put(CustomerCreators.loadAvailableWorkflowActions(fileId)),
    take(CustomerActionTypes.LOAD_WORKFLOW_ACTIONS.END),
  ]);

  yield put(loadFileTrackingItemData({ fileId }));
  yield put(CustomerCreators.stateUpdateSucceeded());
}
export function* rehydrateFileTrackingInfoWatcher() {
  yield takeEvery(
    REHYDRATE_FILE_TRACKING_PAGE.BASE,
    rehydrateFileTrackingInfoWorker,
  );
}

function* pollDocumentsStatus({ fileId }) {
  yield take(AttachmentsActionTypes.GET_DOCUMENTS_STATUS_BY_FILE_ID.END);

  let documentsStatusByFileId = yield select(
    AttachmentsSelectors.getDocumentsStatusByFileIdValue,
    fileId,
  );

  let i = 0;
  const documentWithInProgressStatus = (document) =>
    document.status === DOCUMENT_STATUS.IN_PROGRESS;

  let labels = yield select(CarriersSelectors.labelsValue, {
    fileId,
  });

  while (
    i < 10 ||
    find(documentsStatusByFileId, documentWithInProgressStatus)
  ) {
    yield delay(3000);
    if (isEmpty(get(labels, 'value'))) {
      yield put(CarriersCreators.loadLabels({ fileId }));
    }
    for (const [key] of Object.entries(documentsStatusByFileId ?? {})) {
      yield all([
        put(
          AttachmentsActionCreators.getDocumentsStatusByFileId({
            fileId,
            type: key,
          }),
        ),
        take(AttachmentsActionTypes.GET_DOCUMENTS_STATUS_BY_FILE_ID.END),
      ]);
    }
    documentsStatusByFileId = yield select(
      AttachmentsSelectors.getDocumentsStatusByFileIdValue,
      fileId,
    );
    labels = yield select(CarriersSelectors.labelsValue, {
      fileId,
    });
    i += 1;
  }
  for (const [key, value] of Object.entries(documentsStatusByFileId)) {
    if (value === DOCUMENT_STATUS.UNKNOWN) {
      const meta = { fileId, type: key };

      yield put(
        AttachmentsActionTypes.GET_DOCUMENTS_STATUS_BY_FILE_ID.cancel(meta),
      );
    }
  }
}

function* loadCustomerFile({ fileId, ownerId }) {
  let stepCount = 1;
  let customerInfos;

  do {
    yield put(CustomerCreators.loadCustomerFile({ fileId, ownerId }));
    yield take(CustomerActionTypes.LOAD_CUSTOMER_FILE.END);

    customerInfos = yield select(customerInfosSelector);

    if (stepCount === 2) {
      logVerbose(new Error(`Got an empty currentStatus on GetOwnerFileQuery.`));
    }

    stepCount += 1;

    yield delay(fibonacci(stepCount) * MEDIUM_RELOAD_DELAY);
  } while (
    !customerInfos?.workflowCurrentStatus &&
    stepCount <= MAX_STEP_COUNT
  );
}

export function* loadFileTrackingPage({ payload }) {
  const { fileId } = payload;

  yield fork(loadOrdersDataWorker);

  const ownerId = useLoginStore.getState().login.id;

  yield loadCustomerFile({ fileId, ownerId });

  const file = get(yield select(CustomerSelectors.customerState), ['file']);

  if (file) {
    const fileProducts = get(file, 'fileProducts') || [];
    const claimGroupId = get(file, 'claimGroupId');
    const sellerId = get(file, 'sellerId');
    // load raw product data

    yield all([
      put(ClaimActionCreators.loadConfirmationInfoByClaimGroup(claimGroupId)),
      take(ClaimActionTypes.LOAD_CONFIRMATION_INFO_BY_CLAIM_GROUP.END),
    ]);
    const confirmation = yield select(
      ClaimsSelectors.getClaimGroupsConfirmationValue,
      claimGroupId,
    );
    const storeId = get(confirmation, ['value', 'actorId']) || null;
    const actorType = get(confirmation, ['value', 'actorType']) || null;

    if (storeId && sellerId && actorType === ACTOR_TYPES.STORE) {
      yield put(storeActionCreators.getStore({ storeId }));
    } else if (actorType === ACTOR_TYPES.SUPPLIER) {
      const claimId = get(file, 'claimId');

      yield all([
        put(ClaimActionCreators.getRmaSupplierInfos(claimId)),
        take(ClaimActionTypes.GET_RMA_SUPPLIER_INFOS.END),
      ]);
    }
    for (const { ownerProductId } of fileProducts) {
      yield all([put(loadOwnerProductInfo(ownerProductId))]);
    }
    // load model data (model & brand names)
    const models = uniq(map(fileProducts, (p) => p.modelId));

    for (const modelId of models) {
      yield put(CustomerCreators.loadModelInformations(modelId));
    }
    // Load irsh data (translation for issue/reason/solution)
    const irshIds = uniqBy(
      map(
        fileProducts,
        ({ issueId, reasonId, solutionTypeId, warrantyTypeId }) => ({
          issueId,
          reasonId,
          solutionId: solutionTypeId,
          warrantyTypeId,
        }),
      ),
      JSON.stringify,
    );

    for (const irsh of irshIds) {
      yield put(CustomerCreators.getIssueReasonSolutionNames(irsh));
    }
  }

  yield fork(pollDocumentsStatus, { fileId });

  yield all([
    put(CustomerCreators.loadCustomerFileSummary({ fileId })),
    take(CustomerActionTypes.LOAD_CUSTOMER_FILE_SUMMARY.END),
  ]);

  yield all([
    put(CustomerCreators.getAvailableFileActions(fileId)),
    take(CustomerActionTypes.LOAD_FILE_STATE_HISTORY_LINES.END),
  ]);
  yield all([
    put(CustomerCreators.loadAvailableWorkflowActions(fileId)),
    take(CustomerActionTypes.LOAD_WORKFLOW_ACTIONS.END),
  ]);

  yield all([
    put(CustomerCreators.loadFileStates(fileId)),
    take(CustomerActionTypes.LOAD_FILE_STATES.END),
  ]);

  yield all([
    put(loadFileTrackingItemData({ fileId })),
    put(CarriersCreators.loadLabels({ fileId })),
    put(WorkflowCreators.loadFileHandling({ fileId })),
    take(CarriersActionTypes.LOAD_LABELS.END),
  ]);
}

function* actOnQuoteWorker({ payload }) {
  const {
    answer,
    targetStateName,
    comment,
    quoteId,
    module,
    fromState,
    fileId,
  } = payload;

  yield all([
    put(CustomerCreators.actOnQuote({ answer, quoteId })),
    put(
      ActionCreators.setFileAction({
        module,
        fromState,
        toState: targetStateName,
        fileId,
        comment,
      }),
    ),
    take(CustomerActionTypes.ACT_ON_QUOTE.END),
  ]);
}
function* actOnQuoteWatcher() {
  yield takeEvery(APPROVE_REJECT_QUOTE.BASE, actOnQuoteWorker);
}

function* loadFileTrackingItemDataWorker({ payload }) {
  const { fileId } = payload;

  const customerFileSummary = yield select(
    CustomerSelectors.selectCustomerFileSummary,
  );
  const claimId = get(customerFileSummary, 'claimInfoSummary')?.claimId;
  const { workflowCurrentStatus, moduleSummary } = yield select(
    CustomerSelectors.customerInfosSelector,
  );

  switch (get(workflowCurrentStatus, 'name')) {
    case FILE_STATES.CLAIM_VALIDATED:
    case FILE_STATES.REFUND:
    case FILE_STATES.CLAIM_TO_VALIDATE:
    case FILE_STATES.WAITING_FOR_HANDLING:
      yield all([
        put(CustomerCreators.loadClaimConfirmationScreenData(claimId)),
        take(CustomerActionTypes.LOAD_CLAIM_CONFIRMATION_SCREEN_DATA.END),
      ]);
      break;

    case FILE_STATES.AWAITING_PAYMENT:
      yield all([
        put(CustomerCreators.loadQuotesInfos(fileId)),
        take(CustomerActionTypes.LOAD_QUOTES_DATA.SUCCEEDED),
        put(CustomerCreators.getInvoice({ fileId, moduleSummary })),
        take(CustomerActionTypes.LOAD_INVOICE_DATA.END),
      ]);
      break;

    default:
      yield all([
        put(CustomerCreators.loadClaimConfirmationScreenData(claimId)),
        take(CustomerActionTypes.LOAD_CLAIM_CONFIRMATION_SCREEN_DATA.END),
      ]);
  }
}
export function* loadFileTrackingItemDataWatcher() {
  yield takeEvery(
    LOAD_CURRENT_FILE_TRACKING_ITEM_DATA.BASE,
    loadFileTrackingItemDataWorker,
  );
}

function* submitAdditionalInfoWorker({ payload }) {
  const { fileId } = payload;

  const attachedFiles = yield select(
    AttachmentsSelectors.getUploadedAttachmentsByFileId,
    fileId,
  );
  const attachedFile = head(attachedFiles);

  const file = {
    fileId: payload.fileId,
    transitionInfo: {
      from: payload.fromState,
      to: payload.toState,
      moduleId: get(payload.module, 'id'),
      moduleDefinitionId: get(payload.module, 'definitionId'),
      wave: get(payload.module, 'wave'),
    },
    name: get(attachedFile, ['value', 'name']),
    fileAttachmentId: get(attachedFile, ['value', 'fileAttachmentId']),
    service: 'myaccount',
    extension: get(attachedFile, ['value', 'extension']),
  };

  yield put(AttachmentsActionCreators.setFileAttachmentInfo(file));
}
function* submitAdditionalInfoWatcher() {
  yield takeEvery(SUBMIT_ADDITIONAL_INFO.BASE, submitAdditionalInfoWorker);
}

export function* forkLoadFileTrackingPageWatcher() {
  yield takeEvery(FORK_LOAD_FILE_TRACKING_PAGE.BASE, loadFileTrackingPage);
}

export function* fileTrackingPageSaga() {
  try {
    yield all([
      actOnQuoteWatcher(),
      submitAdditionalInfoWatcher(),
      loadFileTrackingItemDataWatcher(),
      forkLoadFileTrackingPageWatcher(),
      rehydrateFileTrackingInfoWatcher(),
    ]);
  } catch (error) {
    logCritical(error);
  }
}
